import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  InfoLabel,
  InlineDrawer,
  __css,
  Subtitle2,
  Text,
  Textarea,
} from '@fluentui/react-components';
import { useState } from 'react';

import { getClipboard, type Session, setClipboard } from '../api/session';
import {
  isSessionAccessDenied,
  redirectSessionTerminal,
} from '../utils/sessionRedirect';

const useStyles = __css({"inlineButtons":{"mc9l5x":"f22iagw","i8kkvl":0,"Belr9w4":0,"rmohyg":"f1ga9sj3","Byoj8tv":"f1fow5ox"},"flexText":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","i8kkvl":0,"Belr9w4":0,"rmohyg":"f1ga9sj3"}});

interface ClipBoardProps {
  session: Session;
  open: boolean;
  clipboardHasChanged: boolean;
  onClipboardRead: () => void;
  vertical?: boolean;
}

const ClipBoard = ({
  session,
  open,
  clipboardHasChanged,
  onClipboardRead,
  vertical = false,
}: ClipBoardProps) => {
  const styles = useStyles();
  const [text, setText] = useState('');
  const [textHasChanged, setTextHasChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canTransferIn = session.properties.clipboardTransferIn;
  const canTransferOut = session.properties.clipboardTransferOut;

  const handleGetClipboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClipboard(session.id);
      if (typeof data === 'string') {
        setText(data);
        setTextHasChanged(false);
        onClipboardRead();
      } else {
        setError('Received non-text data (image), which is not supported yet.');
      }
    } catch (err) {
      if (isSessionAccessDenied(err)) {
        redirectSessionTerminal(
          session.properties?.seastarLocation,
          'SessionEnd',
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to get clipboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSetClipboard = async () => {
    setLoading(true);
    setError(null);
    try {
      await setClipboard(session.id, text);
      setTextHasChanged(false);
      onClipboardRead();
    } catch (err) {
      if (isSessionAccessDenied(err)) {
        redirectSessionTerminal(
          session.properties?.seastarLocation,
          'SessionEnd',
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to set clipboard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <InlineDrawer
      position={vertical ? 'bottom' : 'end'}
      open={open}
      separator
      surfaceMotion={null}
      style={vertical ? { height: '350px', width: '100%' } : { width: '400px' }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <InfoLabel
            info={
              <div className={styles.flexText}>
                <Subtitle2>Get from the browser</Subtitle2>
                <Text>
                  Select the text in the browser, press{' '}
                  <Text weight="bold">Control+C</Text> on your keyboard, then
                  click the <Text weight="bold">Get from Browser</Text> button.
                </Text>
                <Subtitle2>Send to the browser</Subtitle2>
                <Text>
                  Enter text in the box below, click the{' '}
                  <Text weight="bold">Send to Browser</Text> button, select the
                  field in the browser to add the text to, then press{' '}
                  <Text weight="bold">Control+V</Text> on your keyboard.
                </Text>
              </div>
            }
          >
            Clipboard
          </InfoLabel>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <p>
          Use the text box below to transfer text between your device and the
          isolated browser.
        </p>
        <div className={styles.inlineButtons}>
          <Button
            disabled={!canTransferOut || loading || !clipboardHasChanged}
            onClick={handleGetClipboard}
          >
            Get from Browser
          </Button>
          <Button
            disabled={
              !canTransferIn || loading || !textHasChanged || text.length === 0
            }
            onClick={handleSetClipboard}
          >
            Send to Browser
          </Button>
        </div>
        <Field
          validationState={error ? 'error' : 'warning'}
          validationMessage={
            error || 'Be careful not to transfer sensitive content.'
          }
        >
          <Textarea
            value={text}
            onChange={(_, data) => {
              setText(data.value);
              setTextHasChanged(true);
            }}
            disabled={loading}
          />
        </Field>
      </DrawerBody>
    </InlineDrawer>
  );
};

export default ClipBoard;


import "/usr/local/jenkins/workspace/BI_bi-frontend_2.2.3/src/components/ClipBoard.griffel.css";